/**
 * The library's organization — folders, tags and favourites — shared between an account's devices.
 *
 * Maps travel one by one with their own history (`library.ts`, `engine.ts`). The organization is
 * one small document per account, and it moves as a whole: read what the server has, merge it with
 * what this device has, write the result on top of the revision it was built from.
 *
 * **A merge, never a choice between copies.** Each device remembers the last organization it
 * agreed on with the server (the *base*). What changed here since then and what changed there
 * since then are both kept; only when both sides changed the same folder or the same map does this
 * device's version win (`mergeLibraries`). So a folder made on the phone and a favourite set on the
 * laptop both survive, and a folder deleted on one device is deleted on the other rather than
 * brought back.
 *
 * **Nothing typed while this runs is lost.** The local store is read once at the start. If it has
 * changed by the time the merged result comes back, that change is merged in again rather than
 * written over.
 *
 * **Two devices writing at once cannot both win.** The server applies a write only on the revision
 * it names; the one that loses is told so with what is there now, merges again and retries.
 */
import { EMPTY_LIBRARY, mergeLibraries, parseLibrary, sameLibrary, } from '@orbit-mind/domain';
import { appError, err, ok } from '@orbit-mind/shared-types';
/** A write that lost the race is merged and retried; three losses in a row wait for next time. */
const ATTEMPTS = 3;
function parseStored(value) {
    return value === undefined ? EMPTY_LIBRARY : parseLibrary(JSON.parse(value));
}
function parseBase(value, accountId) {
    if (value === undefined)
        return null;
    try {
        const raw = JSON.parse(value);
        if (typeof raw !== 'object' || raw === null)
            return null;
        const { accountId: owner, revision, organization } = raw;
        if ((owner ?? null) !== accountId)
            return null;
        if (typeof revision !== 'number' || !Number.isInteger(revision) || revision < 0)
            return null;
        return { accountId, revision, organization: parseLibrary(organization) };
    }
    catch {
        // A base that cannot be read is no base: the next pass merges as a first one does, keeping
        // everything on both sides.
        return null;
    }
}
function storageFailure(cause) {
    return appError('STORAGE_UNAVAILABLE', 'the library organization could not be read or saved', {
        retryable: true,
        cause,
    });
}
export async function syncOrganization(options) {
    const accountId = options.accountId ?? null;
    const cancelled = () => options.isCancelled?.() === true;
    let snapshot;
    let base;
    try {
        snapshot = parseStored(await options.local.read());
        base = parseBase(await options.synced.read(), accountId);
    }
    catch (cause) {
        return err(storageFailure(cause));
    }
    const baseOrganization = base?.organization ?? EMPTY_LIBRARY;
    const read = await options.transport.read();
    if (!read.ok)
        return err(read.error);
    let remote = read.value;
    let sent = false;
    for (let attempt = 0; attempt < ATTEMPTS; attempt += 1) {
        if (cancelled())
            return ok({ sent, received: false, revision: base?.revision ?? 0 });
        const remoteOrganization = remote.organization ?? EMPTY_LIBRARY;
        const merged = mergeLibraries(baseOrganization, snapshot, remoteOrganization);
        let revision = remote.revision;
        const serverHasIt = remote.organization === null
            ? sameLibrary(merged, EMPTY_LIBRARY)
            : sameLibrary(merged, remoteOrganization);
        if (!serverHasIt) {
            const written = await options.transport.write(remote.revision, merged);
            if (!written.ok)
                return err(written.error);
            if (written.value.status === 'behind') {
                remote = { revision: written.value.revision, organization: written.value.organization };
                continue;
            }
            revision = written.value.revision;
            sent = true;
        }
        const received = !sameLibrary(merged, snapshot);
        try {
            if (received) {
                // Read again: something may have been filed or renamed while the network answered.
                const current = parseStored(await options.local.read());
                const next = sameLibrary(current, snapshot)
                    ? merged
                    : mergeLibraries(snapshot, current, merged);
                await options.local.write(JSON.stringify(next));
            }
            await options.synced.write(JSON.stringify({ accountId, revision, organization: merged }));
        }
        catch (cause) {
            // The server has the merged organization; the next pass finds it there and writes it down.
            return err(storageFailure(cause));
        }
        return ok({ sent, received, revision });
    }
    return err(appError('REVISION_CONFLICT', 'the library organization kept changing on another device', {
        retryable: true,
    }));
}
//# sourceMappingURL=organization.js.map